import React from 'react';
import moment from 'moment-timezone';
import _ from 'lodash';

export const themeColor = {
  primaryColor: '#21bf25',
  green: '#21bf25',
  red: '#ed3a2a',
  blue: '#3a65e8',
  yellow: '#d8d82f',
  darkGray: '#666',
};

export const formatUUID = (uuid) => {
  if (typeof uuid === 'string') {
    return uuid.substr(0, 8).toUpperCase();
  }
  return uuid;
};

export const unitPrice = (amount, count) => {
  if (count === 0) {
    return 0;
  }
  return amount / count;
};

export const formatNum = (num) => {
  if (typeof num === 'undefined') {
    return '0';
  } else if (typeof num === 'string') {
    return Number(num).toLocaleString();
  }
  return num.toLocaleString();
};

export const convertUtcToJst = (utc) => (utc ? moment(utc).tz('Asia/Tokyo') : utc);

export const formatDate = (dateString, formatStr = 'YYYY/MM/DD HH:mm:ss') =>
  dateString ? moment(dateString).format(formatStr) : dateString;

export const formatJstFromUtc = (utc, defaultString = '') =>
  (!utc || Array.isArray(utc)) ? defaultString : formatDate(convertUtcToJst(utc));

export const formatPostalCode = (postalCode) =>
  `〒${postalCode.slice(0, 3)}-${postalCode.slice(3, 7)}`;

export const toSnakeCase = (str) => str.replace(/([A-Z])/g, (s) => `_${s.charAt(0).toLowerCase()}`);

// XXX: don't want to remove false(bool) value
export const removeEmptyKeys = (obj) =>
  _.pickBy(obj, (v, k) => {
    return v || v === false;
  });

export const isObject = (val) => {
  if (val !== null && typeof val === 'object' && val.constructor === Object) {
    return true;
  }
  return false;
};

export const toArrayQuery = (key, obj) => {
  const keys = Object.keys(obj);
  if (keys.length) {
    const arr = keys
      .map((k) => {
        if (obj[k] === null || obj[k] === '') {
          return null;
        }
        return `${k}=${obj[k]}`;
      })
      .filter((v) => v);
    return `${key}[]=${arr.join(`&${key}[]=`)}`;
  }
  return '';
};

export const toQueryParams = (obj, keyRemoval = false) => {
  const maybeKeyRemoved = keyRemoval ? removeEmptyKeys(obj) : obj;
  return Object.keys(maybeKeyRemoved)
    .map((key) => {
      const k = toSnakeCase(key);
      if (obj[key] == null) {
        return `${encodeURIComponent(k)}=`;
      } else if (moment.isMoment(obj[key])) {
        return `${encodeURIComponent(k)}=${encodeURIComponent(obj[key].toISOString())}`;
      } else if (Array.isArray(obj[key])) {
        return _.join(
          obj[key].map((v) => `${k}[]=${encodeURIComponent(v)}`),
          '&'
        );
      } else if (isObject(obj[key])) {
        if (Object.keys(obj[key]).length <= 0) {
          return `${encodeURIComponent(k)}=`;
        } else if (Object.keys(obj[key]).length > 0) {
          return toArrayQuery(key, obj[key]);
        }
      }
      return `${encodeURIComponent(k)}=${encodeURIComponent(obj[key])}`;
    })
    .join('&');
};

export const toPipeDelimitedQueryParams = (obj, keyRemoval = false) => {
  const maybeKeyRemoved = keyRemoval ? removeEmptyKeys(obj) : obj;
  return Object.keys(maybeKeyRemoved)
    .map((key) => {
      const k = toSnakeCase(key);
      if (obj[key] == null) {
        return `${encodeURIComponent(k)}=`;
      } else if (moment.isMoment(obj[key])) {
        return `${encodeURIComponent(k)}=${encodeURIComponent(obj[key].toISOString())}`;
      } else if (Array.isArray(obj[key])) {
        return `${k}=` + _.join(obj[key].map((v) => `${encodeURIComponent(v)}`), '|');
      } else if (isObject(obj[key])) {
        if (Object.keys(obj[key]).length <= 0) {
          return `${encodeURIComponent(k)}=`;
        } else if (Object.keys(obj[key]).length > 0) {
          return toArrayQuery(key, obj[key]);
        }
      }
      return `${encodeURIComponent(k)}=${encodeURIComponent(obj[key])}`;
    })
    .join('&');
};

export const formatPokeregiName = (name, id) => {
  return !name || name === '' ? `無名のポケレジ (SN:${id})` : name;
};

export const formatPokeregiTopupMode = (mode) => {
  const tbl = {
    disable: '不可',
    enable: '可',
    'enable-if-wired': '接続時可',
  };
  return tbl[mode] || '';
};

export const formatTopupMode = (mode) => {
  const tbl = {
    DISABLED: '設定不可',
    ENABLED: '設定可能',
  };
  return tbl[mode] || '';
};

export const formatDeviceModel = (pcb_ver) => {
  if (!pcb_ver) return '';
  const deviceTypeTbl = {
    PD: 'ポケレジ',
    CAT: 'POS用決済機',
  };
  const [deviceType, version, date] = pcb_ver.split('_');
  return `${deviceTypeTbl[deviceType]}(${version})`;
};

export const formatDeviceFirmwareVersion = (fw_ver) => {
  if (!fw_ver) return '';
  return fw_ver.split('_')[0];
};

export const rewardStatus = (reward) => {
  if (!reward) {
    return {};
  } else if (!reward.done_at && !reward.reserve_at) {
    return { name: 'reward-draft', label: '下書き' };
  } else if (!reward.done_at && reward.reserve_at) {
    return { name: 'reward-reserve', label: '予約' };
  } else if (reward.done_at) {
    return { name: 'reward-done', label: '実行済' };
  }
  // should not come here
  return { name: '', label: '不明' };
};

export const rewardType = (reward) => {
  if (!reward) {
    return '';
  } else if (reward.money_amount > 0) {
    return 'マネー';
  } else if (reward.point_amount > 0) {
    return 'ポイント';
  }
  // should not come here
  return '不明';
};

export const clearingTypeLabel = (clearing) => {
  const clearing_amount = clearing.payment_amount - clearing.invoice_amount;
  if (clearing_amount > 0) {
    return '支払';
  } else if (clearing_amount < 0) {
    return '請求';
  }
  return '精算なし';
};

// 「支払」種別の精算か。clearing_type を優先し、無ければ net(payment − invoice)で判定。
// 一覧・詳細・支払通知書ボタンの表示可否を統一するための共通ヘルパー。
export const isPaymentClearing = (clearing) =>
  clearing.clearing_type
    ? clearing.clearing_type === 'payment'
    : (clearing.payment_amount ?? 0) > (clearing.invoice_amount ?? 0);

// 実際に振り込まれる金額。債権相殺後の実振込額(actual_transfer_amount)を使い、
// 相殺なし(=null)の場合は精算 net(payment − invoice)にフォールバックする。
// 全銀ファイル自体はサーバが作るので、これは「この行が振込対象になるか」を
// 画面側で判定するために使う(相殺で0になった行に口座登録を促さないため)。
export const clearingTransferAmount = (clearing) =>
  clearing.actual_transfer_amount ?? (clearing.payment_amount ?? 0) - (clearing.invoice_amount ?? 0);

// 振込先の口座情報が未登録で、振込データ(全銀ファイル)を作れない精算データか。
// 対象は「未精算の支払」かつ「実振込額 > 0」= 実際に振込対象になる行のみ。
//   - 請求は振込しないため口座情報は不要
//   - 完全相殺で実振込額0になった支払も振込されないので、口座情報の登録を促す必要がない
// 判定する項目はサーバの全銀ファイル生成側(clearing-bank-account-registered-p)と揃えている。
// 振込データ作成時に初めて気づくのではなく、一覧で平時から気づけるようにするための共通ヘルパー。
export const isBankAccountUnregistered = (clearing) => {
  if (clearing.done_at || !isPaymentClearing(clearing)) return false;
  if (clearingTransferAmount(clearing) <= 0) return false;
  return ['bank_code', 'bank_branch_code', 'bank_account', 'bank_account_type'].some(
    (key) => !clearing[key]
  );
};

export const clearingTotalAmount = (clearing) => {
  const clearing_amount = clearing.payment_amount - clearing.invoice_amount;
  if (clearing_amount > 0) {
    return Math.abs(clearing_amount);
  } else if (clearing_amount < 0) {
    return Math.abs(clearing_amount);
  }
  return Math.abs(clearing_amount);
};

export const ThreeDigitSeparator = (amount) => {
  return String(amount).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
};

export const getErrorMessageJp = (type) => {
  var message = '';
  switch (type) {
    case 'account_not_accessible':
      message = 'このユーザーはアカウントにアクセスできません';
      break;
    case 'account_suspended':
      message = 'アカウントは停止されています';
      break;
    case 'account_balance_not_enough':
      message = '口座残高が足りません';
      break;
    case 'account_balance_exceeded':
      message = '口座残高が上限を超えました';
      break;
    case 'account_transfer_limit_exceeded':
      message = '振り込み金額が多すぎます';
      break;
    case 'account_not_for_payment':
      message = 'アカウントは使用できません';
      break;
    case 'account_restricted':
      message = 'アカウントは特定のアカウントの支払いに制限されています';
      break;
    case 'account_not_found':
      message = 'アカウントが見つかりません';
      break;
  }
  return message;
};

export const strConvert = (str, before, after) => {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    let char = str.charAt(i);
    let index = before.indexOf(char);
    if (index >= 0) {
      char = after[index];
    }
    result += char;
  }
  return result;
};

export const zenToHanKatakana = (str) => {
  let zen = new Array(
    'ア',
    'イ',
    'ウ',
    'エ',
    'オ',
    'カ',
    'キ',
    'ク',
    'ケ',
    'コ',
    'サ',
    'シ',
    'ス',
    'セ',
    'ソ',
    'タ',
    'チ',
    'ツ',
    'テ',
    'ト',
    'ナ',
    'ニ',
    'ヌ',
    'ネ',
    'ノ',
    'ハ',
    'ヒ',
    'フ',
    'ヘ',
    'ホ',
    'マ',
    'ミ',
    'ム',
    'メ',
    'モ',
    'ヤ',
    'ヰ',
    'ユ',
    'ヱ',
    'ヨ',
    'ラ',
    'リ',
    'ル',
    'レ',
    'ロ',
    'ワ',
    'ヲ',
    'ン',
    'ガ',
    'ギ',
    'グ',
    'ゲ',
    'ゴ',
    'ザ',
    'ジ',
    'ズ',
    'ゼ',
    'ゾ',
    'ダ',
    'ヂ',
    'ヅ',
    'デ',
    'ド',
    'バ',
    'ビ',
    'ブ',
    'ベ',
    'ボ',
    'パ',
    'ピ',
    'プ',
    'ペ',
    'ポ',
    'ァ',
    'ィ',
    'ゥ',
    'ェ',
    'ォ',
    'ャ',
    'ュ',
    'ョ',
    'ッ'
  );
  let han = new Array(
    'ｱ',
    'ｲ',
    'ｳ',
    'ｴ',
    'ｵ',
    'ｶ',
    'ｷ',
    'ｸ',
    'ｹ',
    'ｺ',
    'ｻ',
    'ｼ',
    'ｽ',
    'ｾ',
    'ｿ',
    'ﾀ',
    'ﾁ',
    'ﾂ',
    'ﾃ',
    'ﾄ',
    'ﾅ',
    'ﾆ',
    'ﾇ',
    'ﾈ',
    'ﾉ',
    'ﾊ',
    'ﾋ',
    'ﾌ',
    'ﾍ',
    'ﾎ',
    'ﾏ',
    'ﾐ',
    'ﾑ',
    'ﾒ',
    'ﾓ',
    'ﾔ',
    'ｲ',
    'ﾕ',
    'ｴ',
    'ﾖ',
    'ﾗ',
    'ﾘ',
    'ﾙ',
    'ﾚ',
    'ﾛ',
    'ﾜ',
    'ｦ',
    'ﾝ',
    'ｶﾞ',
    'ｷﾞ',
    'ｸﾞ',
    'ｹﾞ',
    'ｺﾞ',
    'ｻﾞ',
    'ｼﾞ',
    'ｽﾞ',
    'ｾﾞ',
    'ｿﾞ',
    'ﾀﾞ',
    'ﾁﾞ',
    'ﾂﾞ',
    'ﾃﾞ',
    'ﾄﾞ',
    'ﾊﾞ',
    'ﾋﾞ',
    'ﾌﾞ',
    'ﾍﾞ',
    'ﾎﾞ',
    'ﾊﾟ',
    'ﾋﾟ',
    'ﾌﾟ',
    'ﾍﾟ',
    'ﾎﾟ',
    'ｧ',
    'ｨ',
    'ｩ',
    'ｪ',
    'ｫ',
    'ｬ',
    'ｭ',
    'ｮ',
    'ｯ'
  );
  return strConvert(str, zen, han);
};

export const smallToBigKatakana = (str) => {
  let small = new Array('ｧ', 'ｨ', 'ｩ', 'ｪ', 'ｫ', 'ｬ', 'ｭ', 'ｮ', 'ｯ');
  let big = new Array('ｱ', 'ｲ', 'ｳ', 'ｴ', 'ｵ', 'ﾔ', 'ﾕ', 'ﾖ', 'ﾂ');
  return strConvert(str, small, big);
};

export const zenToHanSymbol = (str) => {
  // 長音'ー'は、半角ハイフン'-'に変換
  let zen = new Array('￥', '，', '．', 'ー', '「', '」', '（', '）', '／', '　', '＼');
  let han = new Array('¥', ',', '.', '-', '｢', '｣', '(', ')', '/', ' ', '\\');
  return strConvert(str, zen, han);
};

export const enMarkToBackSlash = (str) => {
  let enmark = new Array('￥', '¥');
  let backslash = new Array('＼', '\\');
  return strConvert(str, enmark, backslash);
};

export const convertFlicoStringFormat = (str) => {
  return smallToBigKatakana(zenToHanSymbol(zenToHanKatakana(enMarkToBackSlash(str))));
};

export const objectDiff = (obj1, obj2) => _.omitBy(obj1, (v, k) => obj2[k] === v);

export const handleFetchResponse = async (response) => {
  if (!response.ok) {
    throw await response.json();
  }
  return response;
};

export const toFormData = (params) => {
  const formData = new FormData();
  formData.append('_csrf_token', window._csrf_token);
  Object.keys(params).forEach((key) => {
    if (params[key] !== null && params[key] !== undefined) {
      formData.append(key, params[key]);
    }
  });
  return formData;
};

export const applyToTargetValue = (f) => (e) => f(e.target.value);

export const isEmptyObj = (obj) => (obj ? Object.keys(obj).length === 0 : true);

export const randomAlphanumeric = (length) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const ensureInteger = (obj) => {
  const n = parseFloat(obj);
  return Number.isInteger(n) ? Math.floor(n) : null;
};

export const getFrontEnv = () => {
  const hostName = location.hostname;
  if (hostName === 'localhost') {
    return 'local';
  } else if (hostName === 'admin-dev.pokepay.jp') {
    return 'dev';
  } else if (hostName === 'admin-topic1.pokepay.jp') {
    return 'topic1';
  } else if (hostName === 'admin-loadtest.pokepay.jp') {
    return 'loadtest';
  } else if (hostName === 'admin-qa.pokepay.jp') {
    return 'qa';
  } else if (hostName === 'admin-sandbox.pokepay.jp') {
    return 'sandbox';
  } else if (hostName === 'admin.pokepay.jp') {
    return 'production';
  }
  return null;
};

export const ensureArray = value => (
  Array.isArray(value) ? value : [value]
);

export const downloadCsvFile = (downloadUrl, options = {}) => {
  const pattern = /https?:\/\/[-_.!~*\'()a-zA-Z0-9;\/?:\@&=+\$,%#]+/g;

  if (pattern.test(downloadUrl)) {
    const urlItems = downloadUrl.split('/');
    const fileName = options?.fileName ?? urlItems[urlItems.length - 1];

    // Create blob link to download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);
  }
};

export const isNullOrUndifined = v => v == null;
export const dateTimeView = d => (
  !isNullOrUndifined(d) ? moment(d).subtract(1, 's') : null
);
export const endDateTimeFormat = d => (
  !isNullOrUndifined(d)
    ? moment(d).add(1, 'day').hours(0).minute(0).second(0)
    : null
);
